/**
 * DÓNDE ESTÁ EL PARTE, juntando lo que DIJO, la TAREA que tiene en marcha y dónde
 * lo pone el GPS (D28, 22-09-2026; enmienda 24-09-2026).
 *
 * Por orden: lo que dijo («nave 6, sector A, línea 3») manda siempre —quien
 * habla sabe dónde está; el GPS a veces no—. Si no dijo la nave y el parte se
 * hace DESDE una tarea en marcha, la nave es la de la tarea: «aquí» es donde
 * está trabajando. Solo si no hay ni lo uno ni lo otro la pone el GPS. Y lo que
 * ponga el GPS vale solo si encaja debajo: su sector si es la misma nave; su
 * línea si es el mismo sector.
 *
 * El GPS NUNCA pisa en silencio lo dicho ni la tarea (Pablo, 24-09-2026: «si no
 * está con el GPS, pase al audio por si acaso, pero que te ponga un aviso»):
 * cuando dice otra nave u otro sector, `gpsMismatchOf` lo detecta y la pantalla
 * lo avisa con un botón para ir a lo del GPS. Antes (decisión F, 23-09) una nave
 * dicha distinta de la del GPS dejaba todo vacío y preguntaba con dos botones.
 *
 * Y la plaga que entendió la voz NO clasifica: viaja como sugerencia para quien
 * confirma (`voice_guess`).
 *
 * Puro: sin red, sin React. Lo prueba `voicePlacement.test.ts`.
 */

/** Lo que la IA sacó de la nota (ya validado por `voice-create-scouting`). */
export interface VoicePlace {
  locationId: string | null;
  greenhouseId: string | null;
  blockId: string | null;
  lineNumber: number | null;
  /**
   * El sector que DIJO sin decir la nave («línea cuatro del sector A»): sin nave
   * no se sabe cuál de todos los «A» es, así que viaja por su nombre y se busca
   * en la nave donde acabe el parte (`matchSpokenBlock`).
   */
  blockName?: string | null;
}

/** Dónde es la tarea que tiene EN MARCHA quien reporta desde dentro de ella. */
export interface TaskPlace {
  locationId: string | null;
  greenhouseId: string;
  blockId: string | null;
}

/** Dónde dice el GPS que está (`useGpsPlacement`). */
export interface GpsPlace {
  locationId: string | null;
  greenhouseId: string;
  blockId: string | null;
  lineNumber: number | null;
}

export type PlacedBy = "voice" | "task" | "gps" | null;

export interface PlacedFrom {
  greenhouse: PlacedBy;
  block: PlacedBy;
  line: PlacedBy;
}

export interface MergedPlace {
  locationId: string | null;
  greenhouseId: string | null;
  blockId: string | null;
  lineNumber: number | null;
  from: PlacedFrom;
}

/**
 * Idiomas en los que la transcripción no es fiable para sacar un SITIO: el motor
 * (ElevenLabs Scribe v2) no los soporta y en el banco de pruebas de 2026 el mejor
 * modelo para bambara falla casi una palabra de cada dos. Ahí, si hay GPS, manda
 * el GPS; la nota se guarda igual para quien la escuche.
 */
export const VOICE_PLACE_UNRELIABLE_LANGS: ReadonlySet<string> = new Set(["bm", "man"]);

export function mergeVoiceAndGps(
  voice: VoicePlace | null,
  gps: GpsPlace | null,
  opts: { prefer?: "gps"; task?: TaskPlace | null } = {},
): MergedPlace {
  // «Estoy donde dice el GPS»: lo del GPS entero, sin mezclar nada de la otra nave.
  if (opts.prefer === "gps" && gps) return mergeVoiceAndGps(null, gps);
  const task = opts.task ?? null;
  const from: PlacedFrom = { greenhouse: null, block: null, line: null };
  let locationId = voice?.locationId ?? null;
  let greenhouseId: string | null = null;
  const fits = (loc: string | null) => !locationId || !loc || loc === locationId;
  if (voice?.greenhouseId) {
    greenhouseId = voice.greenhouseId;
    from.greenhouse = "voice";
    // La nave dicha sin su finca (no debería pasar: la función la resuelve): la finca de
    // quien esté en esa misma nave.
    if (!locationId && gps && gps.greenhouseId === greenhouseId) locationId = gps.locationId;
    if (!locationId && task && task.greenhouseId === greenhouseId) locationId = task.locationId;
  } else if (task && fits(task.locationId)) {
    // No dijo la nave y está haciendo una tarea: «aquí» es la nave de la tarea.
    greenhouseId = task.greenhouseId;
    locationId = locationId ?? task.locationId;
    from.greenhouse = "task";
  } else if (gps && fits(gps.locationId)) {
    // No dijo la nave ni hay tarea: la del GPS, si no contradice la finca que sí dijo.
    greenhouseId = gps.greenhouseId;
    locationId = locationId ?? gps.locationId;
    from.greenhouse = "gps";
  }

  let blockId = voice?.blockId ?? null;
  if (blockId) {
    from.block = "voice";
  } else if (task?.blockId && greenhouseId && greenhouseId === task.greenhouseId) {
    blockId = task.blockId;
    from.block = "task";
  } else if (gps?.blockId && greenhouseId && greenhouseId === gps.greenhouseId) {
    blockId = gps.blockId;
    from.block = "gps";
  }

  let lineNumber = voice?.lineNumber ?? null;
  if (lineNumber != null) {
    from.line = "voice";
  } else if (gps?.lineNumber != null && blockId && blockId === gps.blockId) {
    lineNumber = gps.lineNumber;
    from.line = "gps";
  }

  return { locationId, greenhouseId, blockId, lineNumber, from };
}

/** El GPS dice OTRO sitio que el que tiene puesto el parte. */
export interface GpsMismatch {
  level: "greenhouse" | "block";
  gpsGreenhouseId: string;
  gpsBlockId: string | null;
}

/**
 * ¿El GPS le pone en otra nave, o en la misma nave pero en otro sector? Se mira
 * contra lo que el parte tiene puesto AHORA —venga de la voz, de la tarea o de
 * que lo eligiera a mano—, así el aviso sigue siendo verdad si cambia algo.
 * La LÍNEA no se compara: a ±25 m el GPS no distingue líneas a 1,5–3 m, y el
 * aviso saldría siempre.
 */
export function gpsMismatchOf(
  place: { greenhouseId: string | null; blockId: string | null },
  gps: GpsPlace | null,
): GpsMismatch | null {
  if (!gps || !place.greenhouseId) return null;
  if (gps.greenhouseId !== place.greenhouseId) {
    return { level: "greenhouse", gpsGreenhouseId: gps.greenhouseId, gpsBlockId: gps.blockId };
  }
  if (gps.blockId && place.blockId && gps.blockId !== place.blockId) {
    return { level: "block", gpsGreenhouseId: gps.greenhouseId, gpsBlockId: gps.blockId };
  }
  return null;
}

const normBlock = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/**
 * El sector dicho por su nombre («A», «sector 3»), dentro de UNA nave. Igual que el
 * rescate de `voice-create-scouting`: nombre exacto ya normalizado, o que acabe en
 * él («Sector A»), y solo si hay UNO; con dos candidatos, ninguno (que lo elija).
 */
export function matchSpokenBlock(spoken: string | null | undefined, blocks: readonly { id: string; name: string }[]): string | null {
  const n = normBlock(spoken ?? "");
  if (!n) return null;
  const tok = n.replace(/^(bloque|blocco|block|sector|settore|seccion|cuartel|modulo)\s+/, "");
  const hits = blocks.filter((b) => {
    const bn = normBlock(b.name);
    return bn === n || bn === tok || bn.endsWith(` ${tok}`);
  });
  const ids = [...new Set(hits.map((b) => b.id))];
  return ids.length === 1 ? ids[0] : null;
}

/**
 * La sugerencia que se guarda con el parte para quien clasifica, Y lo que la voz
 * entendió del sitio aunque no entendiera plaga: comparándolo con dónde acabó el
 * parte se mide si la voz acierta, por idioma (antes, sin plaga no quedaba nada).
 */
export interface VoiceGuess {
  pest_id: string | null;
  pest_name: string | null;
  pest_translation_key: string | null;
  severity: string | null;
  /** De dónde salió cada nivel del sitio: telemetría de si esto acierta. */
  placed: PlacedFrom;
  /** Lo que entendió la voz del sitio (null = no lo entendió). */
  heard?: { greenhouse_id: string | null; block_id: string | null; line: number | null; block_name?: string | null };
  /** Idioma en el que se transcribió. */
  lang?: string | null;
  at: string;
}

export function voiceGuessOf(
  report: {
    pest_id?: string | null;
    pest_name?: string | null;
    pest_translation_key?: string | null;
    severity?: string | null;
    greenhouse_id?: string | null;
    block_ids?: string[] | null;
    observed_row?: number | null;
    spoken_block_name?: string | null;
  } | null | undefined,
  placed: PlacedFrom,
  now: Date = new Date(),
  lang: string | null = null,
): VoiceGuess {
  return {
    pest_id: report?.pest_id ?? null,
    pest_name: report?.pest_name ?? null,
    pest_translation_key: report?.pest_translation_key ?? null,
    severity: report?.severity ?? null,
    placed,
    heard: {
      greenhouse_id: report?.greenhouse_id ?? null,
      block_id: report?.block_ids?.[0] ?? null,
      line: report?.observed_row ?? null,
      block_name: report?.spoken_block_name ?? null,
    },
    lang,
    at: now.toISOString(),
  };
}

/** Lo que se lee de vuelta en la ficha: solo lo que hace falta para preseleccionar. */
export function readVoiceGuess(raw: unknown): { pestId: string; pestName: string | null } | null {
  const g = raw as { pest_id?: unknown; pest_name?: unknown } | null;
  if (!g || typeof g.pest_id !== "string" || !g.pest_id) return null;
  return { pestId: g.pest_id, pestName: typeof g.pest_name === "string" ? g.pest_name : null };
}
